import { JetView } from 'webix-jet'; // Importing JetView from webix-jet framework
import { getCities } from 'models/cities'; // Importing getCities function from cities model
import { getTags } from 'models/tags'; // Importing getTags function from tags model
import { getPositions } from 'models/positions'; // Importing getPositions function from positions model
import 'webix/photo'; // Importing webix photo module
import 'webix/tinymce/tinymce'; // Importing TinyMCE editor from webix
import {
    devConfig, // Importing development configuration
    countries, // Importing countries data
    profile, // Importing user profile data
    cashDesks, // Importing cash desks data
    agentList, // Importing agent list data
} from 'config/config'; // Importing configuration settings

/**
 * FilterFlujoCajaView class extends JetView to create a filter view for cash flow.
 */
export default class FilterFlujoCajaView extends JetView {
    /**
     * Configures the UI components of the filter view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const screen = this.app.config.size; // Screen size configuration
        const left = {
            // Left section configuration
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'text', // Text input for date selection
                    name: 'Datepicker',
                    id: 'Datepicker',
                    css: 'reportrange',
                    readonly: true,
                    label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Localized label
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Localized placeholder
                },
            ],
        };
        const center_left = {
            // Center left section configuration
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'richselect', // Rich select for country selection
                    name: 'CountrySelect',
                    id: 'CountrySelect',
                    localId: 'regions:combo',
                    label: "<span class='icon-language'></span>" + _('País'), // Localized label
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Localized placeholder
                    options: {
                        body: {
                            template: function (obj) {
                                // Template for displaying country flags
                                return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                            },
                            data: countries, // Country data
                        },
                    },
                    // Conditional visibility based on configuration
                    hidden: devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                    css: 'prm-filterByCountry',
                },
                {
                    view: 'richselect', // Second rich select for country selection
                    name: 'CountrySelect2',
                    localId: 'regions:combo',
                    label: "<span class='icon-language'></span>" + _('País'), // Localized label
                    labelPosition: 'top',
                    placeholder: _('Click para seleccionar '), // Localized placeholder
                    value: profile.Settings.ReportCountry, // Default value from profile
                    readonly: true,
                    options: {
                        body: {
                            template: function (obj) {
                                // Template for displaying country flags
                                return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                            },
                            data: countries, // Country data
                        },
                    },
                    // Conditional visibility based on configuration
                    hidden: !devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                    css: 'prm-filterByCountry',
                },
            ],
        };
        const center_right = {
            // Center right section configuration
            margin: 5,
            minWidth: 200,
            rows: [
                {
                    view: 'text', // Text input for ticket number
                    name: 'NoTicket',
                    label: "<span class='icon-ticket'></span>" + _('No. Ticket'), // Localized label
                    labelPosition: 'top',
                    placeholder: _('No. Ticket'), // Localized placeholder
                    invalidMessage: _('No. Ticket is required'), // Validation message
                    type: 'number', // Input type
                },
            ],
        };
        const right = {
            // Right section configuration
            margin: 5,
            minWidth: 200,
            rows: [],
        };
        const upper_section = {
            // Upper section configuration
            id: 'a1',
            rows: [
                {
                    responsive: 'a1',
                    margin: 10,
                    cols: [left, center_left, center_right, right], // Combining sections
                },
            ],
        };
        const buttons = {
            // Button section configuration
            margin: 10,
            cols: [
                {},
                {
                    view: 'button', // Clear button
                    value: _('Limpiar'), // Localized label
                    autowidth: true,
                    click: () => {
                        // Clear action
                        this.getRoot().clear();
                    },
                },
                {
                    view: 'button', // Consult button
                    value: _('Consultar'), // Localized label
                    type: 'form',
                    autowidth: true,
                    click: () => {
                        // Consult action
                        const filterdata = this.getRoot().getValues(); // Get form values
                        if (devConfig.getCountryReport()) {
                            // Conditional logic for country report
                            filterdata.CountrySelect = profile.Settings.ReportCountry;
                        }
                        delete filterdata['CountrySelect2']; // Remove unnecessary field
                        this.app.callEvent('flujoCaja:filter', [filterdata]); // Trigger filter event
                        $$('b5').collapse(); // Collapse the accordion
                    },
                },
            ],
        };
        const accordion_section = {
            // Accordion section configuration
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion', // Accordion view
                    css: 'betShop',
                    collapsed: false,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Localized header
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [upper_section, buttons], // Combining upper section and buttons
                            },
                        },
                    ],
                },
            ],
        };
        return {
            // Returning the main form configuration
            view: 'form',
            type: 'clean',
            rows: [accordion_section],
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} form - The form instance.
     */
    init(form) {
        const lang = this.app.getService('locale').getLang(); // Get current language
        $$('Datepicker').attachEvent('onAfterRender', function () {
            // Event after rendering datepicker
            devConfig.setDateModal(lang); // Set date modal based on language
        });
        webix
            .ajax() // AJAX request to fetch agent countries
            .headers({
                'Content-type': 'application/json', // Setting content type
            })
            .post(devConfig.geturlApi() + 'Agent/GetAgentCountries', '', {
                error: function (text, data, XmlHttpRequest) {
                    // Error handling
                    webix.message('Error! '); // Display error message
                },
                success: function (text, data, XmlHttpRequest) {
                    // Success handling
                    var data = JSON.parse(
                        text
                            .replace(/"Id"/g, '"id"') // Normalize JSON keys
                            .replace(/"Name"/g, '"value"'),
                    );
                    var optionsProvinceRegions = $$('country').getList(); // Get country list
                    optionsProvinceRegions.clearAll(); // Clear existing options
                    optionsProvinceRegions.parse(data.Data); // Parse new data into the list
                },
            });
    }
}

// FILE DOCUMENTED
